/**
 * The arithmetic a declarative game system is allowed to do.
 *
 * `GameSystemRules` already carries formulas as strings — `abilityModFormula`
 * is `"(score - 10) / 2"` and `combat.initiativeFormula` is `"1d20 + dex_mod"`.
 * Nothing has ever evaluated them. This does, and the interesting part is
 * everything it refuses.
 *
 * WHY NOT `eval`, OR `new Function`, OR A LIBRARY: a Tier 2 system is a data
 * file a stranger publishes and a table installs. `@machina/sdk` validates it
 * and Machina runs it, and the entire reason that is safe without a sandbox is
 * that there is no code in it — only numbers, four operators, and a fixed list
 * of functions this file defines. The moment a formula can reach a property, a
 * constructor or a global, "declarative system" stops meaning anything and the
 * Tier 2/Tier 3 line stops being about state and starts being about trust.
 * OpenGamingOS already has a sandboxed `Function()` path for modules that need
 * real code (see the handler runtime); systems that need it are Tier 3.
 *
 * So: a recursive-descent parser over a grammar small enough to read in one
 * sitting, evaluating to a number, with no host objects in scope.
 *
 *   expression := term (('+' | '-') term)*
 *   term       := unary (('*' | '/' | '%') unary)*
 *   unary      := ('-' | '+')? primary
 *   primary    := NUMBER | IDENT | IDENT '(' args ')' | '(' expression ')'
 *   args       := expression (',' expression)*
 *
 * Identifiers resolve against a scope the CALLER builds. A formula can only see
 * what it was handed.
 */
// =============================================================================
// ERRORS
// =============================================================================
/**
 * A formula could not be parsed or evaluated.
 *
 * Thrown rather than returned as a fallback number, because a system whose
 * ability modifier silently evaluates to 0 plays as a different game than the
 * one its author wrote, and nobody at the table can see it happen. A rules
 * document with a broken formula is a broken rules document, and the SDK
 * validator is where that should be caught — this is the backstop.
 */
export class FormulaError extends Error {
    formula;
    constructor(formula, detail) {
        super(`Cannot evaluate formula "${formula}": ${detail}`);
        this.formula = formula;
        this.name = 'FormulaError';
    }
}
// =============================================================================
// THE FUNCTION TABLE
// =============================================================================
/**
 * Every function a formula may call. There is no way to add to this from a
 * rules document — that is the point.
 *
 * `floor` is here because integer division is what most of these systems
 * actually mean: 5e's `(score - 10) / 2` is a floor division, and a system that
 * writes it without saying so gets 2.5 for a score of 15 unless it wraps.
 * Rather than guess, the runtime rounds toward negative infinity at the point
 * of use and lets a formula be explicit when it wants something else.
 */
const FUNCTIONS = {
    floor: { arity: 1, fn: ([n]) => Math.floor(n) },
    ceil: { arity: 1, fn: ([n]) => Math.ceil(n) },
    round: { arity: 1, fn: ([n]) => Math.round(n) },
    abs: { arity: 1, fn: ([n]) => Math.abs(n) },
    min: { arity: 'variadic', fn: (args) => Math.min(...args) },
    max: { arity: 'variadic', fn: (args) => Math.max(...args) },
    /** Clamp, because "at least 1 hit point" and "cap at 6" are everywhere. */
    clamp: { arity: 3, fn: ([n, lo, hi]) => Math.min(Math.max(n, lo), hi) },
};
/** The functions available to a formula, for docs and error messages. */
export const FORMULA_FUNCTIONS = Object.keys(FUNCTIONS);
const IDENT_START = /[A-Za-z_]/;
const IDENT_PART = /[A-Za-z0-9_]/;
const DIGIT = /[0-9]/;
function tokenize(formula) {
    const tokens = [];
    let i = 0;
    while (i < formula.length) {
        const ch = formula[i];
        if (ch === ' ' || ch === '\t' || ch === '\n' || ch === '\r') {
            i++;
            continue;
        }
        if (DIGIT.test(ch) || (ch === '.' && DIGIT.test(formula[i + 1] ?? ''))) {
            let text = '';
            while (i < formula.length && (DIGIT.test(formula[i]) || formula[i] === '.')) {
                text += formula[i++];
            }
            const value = Number(text);
            if (!Number.isFinite(value))
                throw new FormulaError(formula, `"${text}" is not a number`);
            tokens.push({ kind: 'number', value });
            continue;
        }
        if (IDENT_START.test(ch)) {
            let text = '';
            while (i < formula.length && IDENT_PART.test(formula[i]))
                text += formula[i++];
            tokens.push({ kind: 'ident', value: text });
            continue;
        }
        if (ch === '+' || ch === '-' || ch === '*' || ch === '/' || ch === '%') {
            tokens.push({ kind: 'op', value: ch });
            i++;
            continue;
        }
        if (ch === '(') {
            tokens.push({ kind: 'lparen' });
            i++;
            continue;
        }
        if (ch === ')') {
            tokens.push({ kind: 'rparen' });
            i++;
            continue;
        }
        if (ch === ',') {
            tokens.push({ kind: 'comma' });
            i++;
            continue;
        }
        // Anything else — a dot, a bracket, a quote, a backtick — is refused by
        // name rather than skipped, so a formula reaching for a property or a
        // string fails loudly instead of evaluating to something surprising.
        throw new FormulaError(formula, `unexpected character "${ch}" at position ${i}`);
    }
    return tokens;
}
class Evaluator {
    formula;
    tokens;
    scope;
    pos = 0;
    constructor(formula, tokens, scope) {
        this.formula = formula;
        this.tokens = tokens;
        this.scope = scope;
    }
    peek() {
        return this.tokens[this.pos];
    }
    fail(detail) {
        throw new FormulaError(this.formula, detail);
    }
    run() {
        const value = this.expression();
        if (this.pos < this.tokens.length) {
            this.fail(`unexpected trailing input at token ${this.pos}`);
        }
        return value;
    }
    expression() {
        let left = this.term();
        for (;;) {
            const token = this.peek();
            if (token?.kind !== 'op' || (token.value !== '+' && token.value !== '-'))
                return left;
            this.pos++;
            const right = this.term();
            left = token.value === '+' ? left + right : left - right;
        }
    }
    term() {
        let left = this.unary();
        for (;;) {
            const token = this.peek();
            if (token?.kind !== 'op' ||
                (token.value !== '*' && token.value !== '/' && token.value !== '%')) {
                return left;
            }
            this.pos++;
            const right = this.unary();
            if ((token.value === '/' || token.value === '%') && right === 0) {
                this.fail('division by zero');
            }
            left =
                token.value === '*' ? left * right : token.value === '/' ? left / right : left % right;
        }
    }
    unary() {
        const token = this.peek();
        if (token?.kind === 'op' && (token.value === '-' || token.value === '+')) {
            this.pos++;
            const value = this.unary();
            return token.value === '-' ? -value : value;
        }
        return this.primary();
    }
    primary() {
        const token = this.peek();
        if (!token)
            this.fail('unexpected end of formula');
        if (token.kind === 'number') {
            this.pos++;
            return token.value;
        }
        if (token.kind === 'lparen') {
            this.pos++;
            const value = this.expression();
            if (this.peek()?.kind !== 'rparen')
                this.fail('missing closing parenthesis');
            this.pos++;
            return value;
        }
        if (token.kind === 'ident') {
            this.pos++;
            if (this.peek()?.kind === 'lparen')
                return this.call(token.value);
            return this.variable(token.value);
        }
        this.fail(`unexpected token at position ${this.pos}`);
    }
    variable(name) {
        const value = this.scope[name];
        if (typeof value !== 'number' || !Number.isFinite(value)) {
            // Named explicitly, because the usual cause is a formula referring to an
            // ability by a different id than the one the system declared, and "unknown
            // identifier dex_mod" points straight at it.
            this.fail(`unknown identifier "${name}"`);
        }
        return value;
    }
    call(name) {
        const entry = FUNCTIONS[name];
        if (!entry) {
            this.fail(`unknown function "${name}" (available: ${FORMULA_FUNCTIONS.join(', ')})`);
        }
        this.pos++; // consume '('
        const args = [];
        if (this.peek()?.kind !== 'rparen') {
            for (;;) {
                args.push(this.expression());
                if (this.peek()?.kind === 'comma') {
                    this.pos++;
                    continue;
                }
                break;
            }
        }
        if (this.peek()?.kind !== 'rparen')
            this.fail(`missing closing parenthesis after ${name}(`);
        this.pos++;
        if (entry.arity !== 'variadic' && args.length !== entry.arity) {
            this.fail(`${name}() takes ${entry.arity} argument(s), got ${args.length}`);
        }
        if (entry.arity === 'variadic' && args.length === 0) {
            this.fail(`${name}() needs at least one argument`);
        }
        return entry.fn(args);
    }
}
// =============================================================================
// PUBLIC API
// =============================================================================
/**
 * Evaluate a formula against a scope.
 *
 * Throws `FormulaError` for anything it cannot do, including an identifier the
 * scope does not carry. Callers that want a default should catch and say so at
 * their own site — a default buried in here would let a whole system quietly
 * play as zeroes.
 */
export function evaluateFormula(formula, scope = {}) {
    const value = new Evaluator(formula, tokenize(formula), scope).run();
    if (!Number.isFinite(value)) {
        throw new FormulaError(formula, 'evaluated to a non-finite number');
    }
    return value;
}
/**
 * Check a formula without running it, for the SDK validator.
 *
 * `scopeKeys` is the set of identifiers the formula will be given at runtime.
 * Passing them lets an author learn at publish time that they wrote `dex_mod`
 * for a system whose ability is `agility`, rather than at the table.
 */
export function checkFormula(formula, scopeKeys = []) {
    // A scope of zeroes exercises parsing and identifier resolution without
    // needing real character data. Division by zero is the one thing it can
    // report falsely, so that check is relaxed for the dry run.
    const scope = {};
    for (const key of scopeKeys)
        scope[key] = 1;
    try {
        evaluateFormula(formula, scope);
        return { valid: true };
    }
    catch (err) {
        return { valid: false, error: err instanceof Error ? err.message : String(err) };
    }
}
//# sourceMappingURL=expression.js.map